import React, { useState, useContext } from "react";
import PropTypes from "prop-types";
import Popup from "~/assets/js/components/Popup";
import Headline from "~/assets/js/components/Typography/Headline";
import Button from "~/assets/js/components/Button";
import { Check, Edit2 } from "react-feather";
import i18n from "~/assets/js/Helpers/i18n";
import { formatCheckoutPrice } from "~/assets/js/Format";
import FastCheckoutDaySelect from "./FastCheckoutDaySelect";
import {
    CheckoutContext,
    ACTIONS,
    PAGES,
} from "~/assets/js/pages/Checkout/CheckoutProvider";
import { isEmpty } from "lodash";
import { getDeliveryTimes } from "~/assets/js/Api/deliveryTimesApi";
import {
    getFirstAvailableTimeslot,
    formatTimeslotEntryFromCart,
} from "~/assets/js/Helpers/timeslot-utils";
import { useCartContext } from "~/assets/js/global/cartContext";
import { AddToast } from "~/assets/js/components/Toast";
import i18next from "i18next";
import { fetchStoreData } from "../../Api/storeApi";

const FastCheckoutPopup = ({ open, setOpen }) => {
    const [openDaySelect, setOpenDaySelect] = useState(false);
    const { state: contextState, dispatch } = useContext(CheckoutContext);
    const { actions, requiresPayment, cart, isTimeslotSelected } =
        useCartContext();
    const { storePickupAddress } = fetchStoreData(cart?.store_id);

    const timeslotText = formatTimeslotEntryFromCart(
        cart?.slot_begin,
        cart?.slot_end
    );

    const [state, setState] = useState({
        loading: false,
        timeSlotTaken: false,
    });

    const userEmail = cart?.email || "";
    const isPickup = cart?.dispatch_type === "pickup";
    const fullPrice = cart?.value_gross || 0;

    const fastCheckoutClose = () => {
        setOpen(false);
    };

    const popupConfig = {
        open: open,
        customStyle: "fastcheckout-popup",
        isClosable: true,
        handleClose: fastCheckoutClose,
    };

    const moveToNextPage = () => {
        let nextPage = PAGES.CONTACT_DETAILS;

        return dispatch({
            type: ACTIONS.CHANGE_PAGE,
            payload: nextPage,
        });
    };

    const fetchDeliveryTimes = async () => {
        const deliveryTimes = await getDeliveryTimes();

        // Determine if we need to open a popup to notify user of taken slot
        const isSlotTaken = checkSlotTaken(deliveryTimes);

        //Check if timeslots for today are available and redirect back to timeslot
        if (state.slotTaken) {
            checkIfNoTimeSlotsToday(deliveryTimes);
        }

        return isSlotTaken;
    };

    const checkIfNoTimeSlotsToday = (deliveryTimes) => {
        const today = moment().format("YYYY-MM-DD");
        const firstAvailableDay = Object.keys(deliveryTimes)[0];

        if (!(today === firstAvailableDay)) {
            context.dispatch({
                type: ACTIONS.CHANGE_PAGE,
                payload: PAGES.TIME_SLOT_SELECT,
            });
        }
    };

    const checkSlotTaken = (deliveryTimes) => {
        const contextTimeSlot = contextState.timeSlot;
        let taken = false;
        Object.keys(deliveryTimes).map((key) => {
            if (contextTimeSlot.deliveryDay === key) {
                deliveryTimes[key].map((deliveryTime) => {
                    if (contextTimeSlot.begin === deliveryTime.begin) {
                        let nextDeliverySlot = getFirstAvailableTimeslot(
                            deliveryTimes[key]
                        );

                        if (!deliveryTime.free) {
                            taken = true;
                            setState({
                                ...state,
                                timeSlotTaken: true,
                                nextDeliverySlot: nextDeliverySlot,
                            });
                        }
                    }
                });
            }
        });
        return taken;
    };

    const handleSubmitOrder = async () => {
        setState({ ...state, loading: true });

        const isSlotTaken = await fetchDeliveryTimes();

        if (isSlotTaken) {
            setState({ ...state, loading: false });
            return;
        }

        const requiredPaymentButNotProvided =
            requiresPayment &&
            (isEmpty(cart?.payment) || cart?.payment?.method === "none");

        if (requiredPaymentButNotProvided) {
            dispatch({
                type: ACTIONS.CHANGE_PAGE,
                payload: PAGES.PAYMENT_SELECT,
            });
            return;
        }

        const fastCheckoutDefaultValues = {
            deliveryInstructions: {
                contactless_delivery: cart?.shipping?.contactless_delivery,
                give_porter: cart?.shipping?.give_porter,
                delivery_phone_call: cart?.shipping?.delivery_phone_call,
                no_doorbell: cart?.shipping?.no_doorbell,
            },
            comment: "",
        };

        const response = await actions.checkoutCart(
            fastCheckoutDefaultValues.deliveryInstructions,
            fastCheckoutDefaultValues.comment
        );

        if (!response || !response.ok) {
            setState({ ...state, loading: false });
            return dispatch(
                AddToast(
                    "danger",
                    i18n.t("orderConfirmation.submitFailed"),
                    response?.error ? response.error : ""
                )
            );
        }

        // Redirect to correct page after submitting order.
        window.location.href = response.redirect_to;
    };

    const renderBillingForPickup = () => {
        return (
            <>
                <div>
                    {cart?.billing?.first_name} {cart?.billing?.last_name}
                </div>
                <div>Am {cart?.billing?.address}</div>
                <div>
                    {cart?.billing?.postcode} {cart?.billing?.city}
                </div>
            </>
        );
    };

    const renderBillingForDelivery = () => {
        const deliveryMatchesBilling =
            cart?.billing?.address === cart?.shipping?.address &&
            cart?.billing?.city === cart?.shipping?.city &&
            cart?.billing?.postcode === cart?.shipping?.postcode;
        if (deliveryMatchesBilling) {
            return (
                <div>
                    {i18next.t(`cart.fastCheckoutPopup.adressAsDelivery`)}
                </div>
            );
        }
        return (
            <>
                <div>
                    {cart?.billing?.first_name} {cart?.billing?.last_name}
                </div>
                <div>Am {cart?.billing?.address}</div>
                <div>
                    {cart?.billing?.postcode} {cart?.billing?.city}
                </div>
                <div>{cart?.phone}</div>
            </>
        );
    };

    return (
        <>
            <Popup {...popupConfig}>
                <div style={{ position: "relative" }}>
                    <div className="fastcheckout-popup__title">
                        <Headline size={6} className="headline">
                            {i18n.t("cart.fastCheckoutPopup.title")}
                        </Headline>
                        <div className="email">{userEmail}</div>
                    </div>

                    <div className="fastcheckout-popup__upcontent">
                        <div className="line">
                            <div className="line__title">
                                {isPickup
                                    ? i18n.t(
                                          "cart.fastCheckoutPopup.pickUpAddress"
                                      )
                                    : i18n.t(
                                          "cart.fastCheckoutPopup.deliveryAddress"
                                      )}
                            </div>
                            <div className="line__information">
                                {isPickup ? (
                                    <div>{storePickupAddress}</div>
                                ) : (
                                    <>
                                        <div>
                                            {cart?.shipping?.first_name}{" "}
                                            {cart?.shipping?.last_name}
                                        </div>
                                        <div>Am {cart?.shipping?.address}</div>
                                        <div>
                                            {cart?.shipping?.postcode}{" "}
                                            {cart?.shipping?.city}
                                        </div>
                                        <div>{cart?.phone}</div>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="line">
                            <div className="line__title">
                                {i18next.t(`cart.fastCheckoutPopup.billAdress`)}
                            </div>
                            <div className="line__information">
                                {isPickup
                                    ? renderBillingForPickup()
                                    : renderBillingForDelivery()}
                            </div>
                        </div>
                        <div className="line">
                            <div className="line__title">
                                {i18next.t(
                                    `cart.fastCheckoutPopup.paymentMethod`
                                )}
                            </div>
                            <div className="line__information">
                                <div>
                                    {i18n.t(
                                        `cart.payment.${cart?.payment?.method}`
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="line">
                            <div className="line__title">
                                {i18next.t(
                                    `cart.fastCheckoutPopup.deliveryWindow`
                                )}
                            </div>
                            <div className="line__information">
                                {isTimeslotSelected && (
                                    <div className="working-hours">
                                        {timeslotText}
                                    </div>
                                )}

                                {!isPickup && (
                                    <div className="delivery">
                                        {i18next.t(
                                            `cart.fastCheckoutPopup.earlyDelivery`
                                        )}
                                    </div>
                                )}

                                <Button
                                    customStyle={"time-slot"}
                                    label={i18n.t(
                                        "cart.fastCheckoutPopup.changeTimeButton"
                                    )}
                                    icon={<Edit2 size={18} color={"#00E940"} />}
                                    onClick={() => {
                                        setOpenDaySelect(true);
                                        fastCheckoutClose();
                                    }}
                                    variant="secondary"
                                    size="XS"
                                />
                            </div>
                        </div>

                        {!isPickup ? (
                            <div className="line">
                                <div className="line__title">
                                    {i18n.t("cart.fastCheckoutPopup.remarks")}
                                </div>
                                <div className="line__information">
                                    <div className="line__information--hidden">
                                        {i18n.t(
                                            "cart.fastCheckoutPopup.deliveryDescription"
                                        )}
                                    </div>

                                    <div className="line__information--options">
                                        <Check />
                                        <div className="option">
                                            {i18n.t(
                                                "cart.fastCheckoutPopup.deliveryInstructionOne"
                                            )}
                                        </div>
                                    </div>
                                    <div className="line__information--options">
                                        <Check />
                                        <div className="option">
                                            {i18n.t(
                                                "cart.fastCheckoutPopup.deliveryInstructionTwo"
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="line">
                                <div className="line__text">
                                    {i18n.t(
                                        "cart.fastCheckoutPopup.pickupTerms"
                                    )}
                                    <a href="https://pottsalat.de/agb/">
                                        {" "}
                                        {i18n.t(
                                            "cart.fastCheckoutPopup.pickupTermsLinkOne"
                                        )}
                                    </a>{" "}
                                    <a href="https://pottsalat.de/datenschutz/">
                                        {i18n.t(
                                            "cart.fastCheckoutPopup.pickupTermsLinkTwo"
                                        )}
                                    </a>
                                    und den{" "}
                                    <a href="">
                                        {i18n.t(
                                            "cart.fastCheckoutPopup.pickupTermsLinkThree"
                                        )}
                                    </a>{" "}
                                    einverstanden.
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="fastcheckout-popup__downcontent">
                        <Button
                            customStyle={"popup_price"}
                            label={i18n.t("cart.fastCheckoutPopup.payButton")}
                            icon={
                                <div className="button_price">
                                    {formatCheckoutPrice(fullPrice, 0, "€")}
                                </div>
                            }
                            onClick={handleSubmitOrder}
                            disabled={!isTimeslotSelected}
                            loading={state.loading}
                        />

                        <Button
                            customStyle={"edit_details"}
                            label={i18n.t(
                                "cart.fastCheckoutPopup.editDetailsButton"
                            )}
                            onClick={moveToNextPage}
                        />
                    </div>
                </div>
            </Popup>
            <FastCheckoutDaySelect
                open={openDaySelect}
                setOpen={setOpenDaySelect}
                setOpenFastCheckout={setOpen}
            />
        </>
    );
};

FastCheckoutPopup.propTypes = {
    open: PropTypes.bool,
    setOpen: PropTypes.func,
    cart: PropTypes.object,
};

export default FastCheckoutPopup;
